/* RESPONSIBLE TEAM: team-automation-foundations */
import Step from 'embercom/models/operator/visual-builder/step';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import { buildValidations, validator } from 'ember-cp-validations';
import { type SyncHasMany } from '@ember-data/model';
import { type Block } from 'embercom/models/common/blocks/block';
import type Store from '@ember-data/store';
import { TargetContext } from 'embercom/objects/operator/configuration/custom-bot';
import { SUPPORTED_CHANNELS } from 'embercom/lib/operator/custom-bots/constants-2';
import { LOCAL_VARIABLE_IDENTIFIER_PREFIX } from 'embercom/models/operator/visual-builder/attribute-descriptor';
import { extractTemplateVariables } from 'embercom/lib/workflows/template-variables';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';

export const noteStepBlockValidators = {
  blocks: [
    validator('blocks-not-empty', {
      message: 'operator.workflows.visual-builder.validations.blank-message-error',
      messageTranslated: true,
    }),
    validator('standalone/block-fields-available-in-production', {
      isWarning: true,
      lazy: false,
      messageKey: 'standalone.common.field-unavailable-in-production.warning-message',
    }),
  ],
};

const Validations = buildValidations({
  ...noteStepBlockValidators,
});

export default class Note extends Step.extend(Validations) {
  @fragmentArray('common/blocks/block', { polymorphic: true, typeKey: 'modelKey' })
  declare blocks: SyncHasMany<Block>;

  @computed('blocks')
  get localVariableIdentifiersUsed() {
    let templateVariables = extractTemplateVariables(this.blocks.toArray());
    return templateVariables.filter((variable) =>
      variable.startsWith(LOCAL_VARIABLE_IDENTIFIER_PREFIX),
    );
  }

  get supportedChannels() {
    return SUPPORTED_CHANNELS.note;
  }

  static get supportedContexts() {
    return [
      TargetContext.Conversation,
      TargetContext.CustomerTicket,
      TargetContext.BackOfficeTicket,
      TargetContext.TrackerTicket,
    ];
  }

  static get requiredBillingFeature() {
    return 'workflows_core_billing_feature';
  }

  get serializedBlocks() {
    // We only need to serialize the blocks if they are a FragmentArray (in tests they are already serialized)
    if (this.blocks.type === 'common/blocks/block') {
      return this.blocks.serialize();
    } else {
      return this.blocks;
    }
  }

  static createNewStep(store: Store): Note {
    let blocks = [
      store.createFragment('common/blocks/paragraph', {
        type: 'paragraph',
        text: '', // let the component decide what the default or placeholder text should be
      }),
    ];

    return store.createRecord('operator/visual-builder/step/note', {
      blocks,
      type: 'operator/visual-builder/step/note',
    });
  }
}
